<template>
  <div
    v-if="Object.keys(row).length"
    :id="rowId"
    :class="[{'plp-page-row cell':isPlpPage === 'discovery'},
             {'home-page-row grid-container': isPlpPage !== 'discovery'}]"
  >
    <div
      ref="zoneContainer"
      :class="['grid-x zones-container', ...generalClassesArray]"
    >
      <div
        v-if="isZoneOfRelevanceRow"
        class="cell"
      >
        <slot name="zone-of-relevance" />
      </div>
      <div
        v-else-if="isSortableGridRow"
        id="canvas"
        class="cell"
      >
        <GoogleAdPlaceholder
          v-if="isMcom && isGoogleAdEnabled"
          ad-id="googleAdOnSearch"
          :page="pageType"
          :device="deviceType"
          :is-u-s-a-region="isUSARegion"
          :is-wedding-registry="isWeddingRegistry"
          :env-brand="brand"
          :category-id="categoryId"
          class="grid-container"
        />
        <slot name="sortable-grid" />
      </div>
      <MediaTypes
        v-else-if="!isOldDashboardRow"
        :zones="zonesData"
        :meta="meta"
        :row-attributes="rowObj?.attributes"
        :ks-meta="ksMeta"
        :is-plp-page="isPlpPage"
      />
    </div>
  </div>
</template>

<script setup>
import {
  computed, provide, ref, onMounted,
} from 'vue';
import { GoogleAdPlaceholder } from '@common-vue/site-monetization';
import { brand } from 'config';
import MediaTypes from './MediaTypes';

const props = defineProps({
  row: {
    type: Object,
    default: () => ({}),
  },
  rowId: {
    type: String,
    default: '',
  },
  meta: {
    type: Object,
    default: () => ({}),
  },
  isPlpPage: {
    type: String,
    default: null,
  },
  fetchPriority: {
    type: Boolean,
    default: false,
  },
  ksMeta: {
    type: Object,
    default: () => ({}),
  },
});

const zoneContainer = ref(null);

const classesByRowTypeIds = {
  102: 'small-up-1 medium-up-2 grid-margin-x',
  103: 'small-up-1 medium-up-3 grid-margin-x',
  104: 'small-up-2 medium-up-4 grid-margin-x',
  105: 'small-up-1 medium-up-4 large-up-5 grid-margin-x',
  106: 'small-up-2 medium-up-3 large-up-6 grid-margin-x',
};

const classesByColumnTypeIds = {
  102: 'small-up-1 medium-up-2 grid-margin-x',
  103: 'small-up-1 medium-up-1 column-margin large-up-3 grid-margin-x',
};

const alwaysFullWidthZoneTypes = {
  messageInset: true,
  largeCarousel: true,
};

const isEqualTrue = (value) => value?.toString() === 'true';
const getImageData = (wrapper) => {
  if (wrapper?.imageMap) {
    return wrapper.imageMap;
  }
  if (wrapper?.banner) {
    return wrapper.banner;
  }
  return null;
};

const isMcom = brand === 'mcom';
const isGoogleAdEnabled = props.ksMeta?.pageType?.browse || props.ksMeta?.pageType?.subSplash;
let pageType = '';
if (props.ksMeta?.pageType?.subSplash) {
  pageType = 'SUB_SPLASH';
} else if (props.ksMeta?.pageType?.browse) {
  pageType = 'BROWSE';
}
const categoryId = props.ksMeta?.analytics?.data?.categoryID || '';
const deviceType = props.ksMeta?.context?.deviceType === 'DESKTOP' ? 'desktop' : 'mobile';
const isUSARegion = props.ksMeta?.context?.regionCode === 'US';
const isWeddingRegistry = props.ksMeta?.context?.shoppingMode === 'WEDDING_REGISTRY';

const isZoneOfRelevanceRow = computed(() => props.row?.row140?.attributes?.isZorDashboard);
const rowObj = computed(() => props.row?.[Object.keys(props.row || {})?.[0]]);
const zonesData = computed(() => {
  const zones = rowObj.value?.zones;
  const isRow105Carousel = rowObj.value?.rowTypeId === 105
    && props.meta?.properties?.transformRow105ToCarousel
    && props.meta?.context?.deviceType !== 'DESKTOP'
    && zones?.[0]?.carousel;

  if (isRow105Carousel) {
    zones[0].carousel.attributes = {
      ...(zones[0].carousel.attributes || {}),
      ...(getImageData(zones[0].carousel.cards?.[0])?.attributes || {}),
      colCount: '5',
    };
  }
  return zones;
});

const generalClassesArray = computed(() => {
  const isMultiZonesRow = [102, 103, 104, 105, 106].some((rowId) => rowId === rowObj.value?.rowTypeId);
  const PLPZoneType = Object.keys(zonesData.value?.[0] || {})[0];
  const zoneType = zonesData.value?.[0]?.type || PLPZoneType;
  const firstZoneAttributes = zonesData.value?.[0]?.[zoneType]?.attributes;
  const rowAttributes = rowObj.value?.attributes;

  const columnLayout = rowObj.value?.attributes?.DISPLAY === 'COLUMN_LAYOUT';
  const isFullWidth = alwaysFullWidthZoneTypes[zoneType]
    || (!isMultiZonesRow
    && !isZoneOfRelevanceRow.value
    && [firstZoneAttributes?.fullWidth, rowAttributes?.fullWidth].some(isEqualTrue));
  const isMarginBottom = [firstZoneAttributes?.bottomMargin, rowAttributes?.bottomMargin].some(isEqualTrue);
  const isPaddingBottom = [firstZoneAttributes?.bottomPadding, rowAttributes?.bottomPadding].some(isEqualTrue);
  const isTopPadding = [firstZoneAttributes?.topPadding, rowAttributes?.topPadding].some(isEqualTrue);

  return [
    isFullWidth ? 'full-width' : 'not-full-width',
    isMarginBottom && 'margin-bottom',
    isPaddingBottom && 'padding-bottom',
    isTopPadding && 'padding-top',
    columnLayout ? classesByColumnTypeIds[rowObj.value?.rowTypeId] : classesByRowTypeIds[rowObj.value?.rowTypeId],
  ].filter(Boolean);
});

provide('fetchPriority', props.fetchPriority);
const isSortableGridRow = computed(() => props.row?.rowSortableGrid);
const isOldDashboardRow = computed(() => props.row?.row130?.attributes?.DISPLAY === 'DASHBOARD');

/* istanbul ignore next */
const clearSpacingForEmptyRow = () => {
  if (zoneContainer.value && props.isPlpPage === 'discovery') {
    const child = zoneContainer.value.children[0];
    if (child && child?.classList.contains('cell') && !child.querySelector('div')) {
      if (zoneContainer.value.classList.contains('padding-bottom')) {
        zoneContainer.value.style.setProperty('padding-bottom', '0', 'important');
      }
      if (zoneContainer.value.classList.contains('margin-bottom')) {
        zoneContainer.value.style.setProperty('margin-bottom', '0', 'important');
      }
    }
  }
};

onMounted(() => {
  clearSpacingForEmptyRow();
});
</script>

<style lang="scss" scoped>
.grid-container{
  padding: 0;
}
.home-page-row {
  overflow: hidden;
}

.column-margin {
  @include breakpoint(medium only) {
    > :deep(.cell:not(:last-child)) {
      margin-bottom: rem-calc(32px);
    }
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@import '../scss/globalAttributes.mcom.scss';
</style>

<style lang="scss" brand="bcom" scoped>
@import '../scss/globalAttributes.bcom.scss';

.no-margin-bottom {
  margin-bottom: 0px;
}

.no-padding-bottom {
  padding-bottom: 0px;
}
</style>
